/**
 * Saved views — persistence for named list filters (`savedViews`).
 *
 * WHY this exists: the power-user teardown flagged that no list surface
 * offers filters/sort/saved views. The PURE engine that interprets a
 * view lives in src/lib/crm/viewFilter.ts; this module is the other
 * half — a tiny store wrapper so a named filter+sort spec survives
 * reload and syncs like everything else.
 *
 * WHY the id is `${entity}:${slug(name)}`: a saved view is naturally
 * unique by (entity, name) — "Hot deals" on accounts is ONE view, and
 * re-saving it should replace it, not pile up duplicates. Keying the
 * record deterministically makes every save an upsert, exactly like
 * healthHistory keys its rows by `${personId}:${date}`.
 *
 * Honest accounting: nothing is seeded — an empty list means the user
 * has saved no views. In mock mode this lives in localStorage and flips
 * to Firestore with the same flag as the rest of the app. Mirrors
 * src/lib/db/healthHistory.ts. View wiring into the list UI is a later,
 * gated atom — this module is logic only.
 */

import { db, nowIso } from './store';
import type { SavedView, SavedViewEntity } from './types';
import type { ViewFilter, ViewSort } from '../crm/viewFilter';

/** Everything the caller provides; id + createdAt are derived here. */
export interface SavedViewInput {
  name: string;
  entity: SavedViewEntity;
  filters: ViewFilter[];
  sort?: ViewSort;
}

/** Deterministic doc id per (entity, name) — makes saves upserts. */
function savedViewId(entity: SavedViewEntity, name: string): string {
  const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return `${entity}:${slug}`;
}

/** Stable name ordering for lists + subscriptions (case-insensitive). */
function byName(a: SavedView, b: SavedView): number {
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
}

/**
 * Save a view. Upserts by (entity, name): re-saving an existing name
 * replaces its filters/sort but keeps the original id + createdAt, so
 * references to the view stay stable. Returns the persisted record.
 */
export async function saveView(input: SavedViewInput): Promise<SavedView> {
  const id = savedViewId(input.entity, input.name);
  const existing = await db.get('savedViews', id);
  const view: SavedView = {
    id,
    name: input.name,
    entity: input.entity,
    filters: input.filters,
    createdAt: existing?.createdAt ?? nowIso(),
    ...(input.sort ? { sort: input.sort } : {}),
  };
  await db.put('savedViews', view);
  return view;
}

/** All saved views (optionally for one entity), sorted by name. */
export async function listSavedViews(entity?: SavedViewEntity): Promise<SavedView[]> {
  const all = await db.list('savedViews');
  return all.filter((v) => !entity || v.entity === entity).sort(byName);
}

/** Delete one saved view by id. */
export async function removeSavedView(id: string): Promise<void> {
  await db.remove('savedViews', id);
}

/** Subscribe to saved views (optionally one entity), sorted by name. */
export function subscribeSavedViews(
  fn: (views: SavedView[]) => void,
  entity?: SavedViewEntity,
): () => void {
  return db.subscribe('savedViews', (rows) =>
    fn(rows.filter((v) => !entity || v.entity === entity).sort(byName)),
  );
}
